// A creation type = its schema (contracts), its consistency and safety checks, its key figures
// and what the workshop must know to generate one. Everything else (catalogue, publishing, generation,
// library) is generic and only knows this interface.
import type { z } from 'zod/v4';
import type { CreationKind, CreationOf, Fact, Issue } from '@gruvetto/contracts';

export type Effort = 'low' | 'medium' | 'high';

export interface KindContext {
  /** The player's FTP, to express workouts in watts. */
  ftp: number;
  /** Finds a workout by id (library, player's collection). */
  findWorkout?: (id: string) => CreationOf<'workout'> | null;
}

export interface KindAi<K extends CreationKind> {
  /** What this type is, in one sentence, for the automatic classification of requests. */
  summary: string;
  /** Generation instructions (format, units, good practices of the domain). */
  brief: string;
  /** Complete and valid example, shown to the model. */
  example: CreationOf<K>;
  effort: Effort;
  maxTokens: number;
  /** Schema of the content requested from the model if it differs from the published schema (simpler subset to produce). */
  schema?: z.ZodType;
}

export interface KindDef<K extends CreationKind = CreationKind> {
  kind: K;
  label: string; plural: string; icon: string;
  /** Verb of the main button ("Ride", "Play", "Wear"). */
  verb: string;
  /** Checks beyond the schema; an error prevents publishing, a warning is displayed. */
  check(c: CreationOf<K>, ctx: KindContext): Issue[];
  facts(c: CreationOf<K>, ctx: KindContext): Fact[];
  ai: KindAi<K>;
}

export const defineKind = <K extends CreationKind>(k: KindDef<K>): KindDef<K> => k;
export const err = (message: string, path?: string): Issue => ({ level: 'error', message, ...(path ? { path } : {}) });
export const warn = (message: string, path?: string): Issue => ({ level: 'warning', message, ...(path ? { path } : {}) });

export const fmtMin = (s: number) => { const m = Math.round(s / 60); return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}` : `${m} min`; };
